import {type VariantProps} from 'class-variance-authority';
import {ToggleGroup as ToggleGroupPrimitive} from 'radix-ui';
import * as React from 'react';

import {cn} from '@/utils/ui';

import {toggleVariants} from '../toggle';
import {toggleGroupVariants} from './toggle-group-variants';

const ToggleGroupContext = React.createContext<
  Pick<VariantProps<typeof toggleVariants>, 'size' | 'variant'>
>({
  size: 'default',
  variant: 'default',
});

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleGroupVariants> & {
      itemVariant?: VariantProps<typeof toggleVariants>['variant'];
      size?: VariantProps<typeof toggleVariants>['size'];
    }
>(({className, variant, size, itemVariant, children, ...props}, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn(toggleGroupVariants({variant}), className)}
    {...props}
  >
    <ToggleGroupContext.Provider value={{variant: itemVariant, size}}>
      {children}
    </ToggleGroupContext.Provider>
  </ToggleGroupPrimitive.Root>
));

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleVariants>
>(({className, children, variant, size, ...props}, ref) => {
  const context = React.useContext(ToggleGroupContext);

  const resolvedVariant = context.variant || variant;
  const resolvedSize = context.size || size;

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({variant: resolvedVariant, size: resolvedSize}),
        className
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export {ToggleGroup, ToggleGroupItem};
